import React from 'react';
import { Alert, Form, Input, InputNumber, Radio, Typography } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { RouteOrderDto } from '../../../types/transport';
import { formatLocalizedQuantity } from '../../../utils/numberFormatting';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from '../../../utils/polishNumberInput';
import { formatUnit } from '../../../utils/units';
import { formatLineDimensions, type LineDimensionSource } from '../../../utils/productDimensions';
import './TransportDeliveryQuantityFields.css';

const { Text } = Typography;

export interface TransportDeliveryCargoItem extends LineDimensionSource {
  orderId: string;
  orderProductId?: string;
  productName: string;
  quantity: number;
  unit?: string;
  deliveredQuantity?: number | null;
}

/**
 * Why a line delivered less than planned (plan 2026-10-13, backend
 * transportDeliveryPolicy): NOT_ARRIVED keeps the plan and releases the rest
 * for another truck; NOT_ON_TRUCK cuts the plan to the delivery (0 = the line
 * leaves the truck).
 */
export type TransportDeliveryShortfall = 'NOT_ARRIVED' | 'NOT_ON_TRUCK';

export interface TransportDeliveryFormItem {
  orderProductId: string;
  deliveredQuantity: number;
  shortfall?: TransportDeliveryShortfall | null;
}

/** The line delivered less than planned, so the operator says why. */
export function isShortDelivery(plannedQuantity: number, deliveredQuantity: number): boolean {
  return Number.isFinite(deliveredQuantity) && deliveredQuantity < plannedQuantity - 0.0005;
}

export function formatDeliveryQuantityInput(value?: string | number | null): string {
  const normalized = String(value ?? '').replace('.', ',');
  if (!normalized.includes(',')) return normalized;
  return normalized.replace(/0+$/, '').replace(/,$/, '');
}

export function buildTransportDeliveryFormItems(
  cargoItems: TransportDeliveryCargoItem[] = [],
): TransportDeliveryFormItem[] {
  return cargoItems
    .filter((item): item is TransportDeliveryCargoItem & { orderProductId: string } => Boolean(item.orderProductId))
    .map((item) => {
      const deliveredQuantity = item.deliveredQuantity == null
        ? Number(item.quantity || 0)
        : Number(item.deliveredQuantity);
      return {
        orderProductId: item.orderProductId,
        deliveredQuantity,
        // A saved short delivery kept its plan: it was „Nie dojechało”.
        shortfall: isShortDelivery(Number(item.quantity || 0), deliveredQuantity) ? 'NOT_ARRIVED' as const : null,
      };
    });
}

interface Props {
  cargoItems: TransportDeliveryCargoItem[];
  routeOrders?: RouteOrderDto[];
  name?: string;
}

const TransportDeliveryQuantityFields: React.FC<Props> = ({
  cargoItems = [],
  routeOrders = [],
  name = 'deliveryItems',
}) => {
  const { t, i18n } = useTranslation(['transport']);
  const orderNumberById = new Map(routeOrders.map((routeOrder) => [
    routeOrder.orderId,
    routeOrder.order.displayOrderNumber || routeOrder.order.orderNumber,
  ]));
  const missingIdentity = cargoItems.some((item) => !item.orderProductId);

  return (
    <section className="transport-delivery-quantity-fields">
      <div className="transport-delivery-quantity-fields__intro">
        <strong>{t('transport:deliveryConfirmation.title')}</strong>
        <Text type="secondary">{t('transport:deliveryConfirmation.description')}</Text>
      </div>

      {routeOrders.length > 1 ? (
        <aside className="transport-delivery-quantity-fields__shared">
          <WarningOutlined />
          <div>
            <strong>{t('transport:deliveryConfirmation.sharedTitle')}</strong>
            <span>{t('transport:deliveryConfirmation.sharedDescription', {
              orders: routeOrders
                .map((routeOrder) => routeOrder.order.displayOrderNumber || routeOrder.order.orderNumber)
                .filter(Boolean)
                .join(', '),
            })}</span>
          </div>
        </aside>
      ) : null}

      {missingIdentity ? (
        <Alert
          type="error"
          showIcon
          message={t('transport:deliveryConfirmation.missingProductLink')}
        />
      ) : null}

      <Form.List name={name}>
        {(fields) => (
          <div className="transport-delivery-quantity-fields__list">
            {fields.map((field, index) => {
              const cargo = cargoItems[index];
              if (!cargo?.orderProductId) return null;
              const plannedQuantity = Number(cargo.quantity || 0);
              const unit = formatUnit(cargo.unit, i18n.language);
              const orderNumber = orderNumberById.get(cargo.orderId);
              const dimensions = formatLineDimensions(cargo);

              return (
                <div className="transport-delivery-quantity-fields__row" key={cargo.orderProductId}>
                  <div className="transport-delivery-quantity-fields__product">
                    <strong>{cargo.productName}</strong>
                    {dimensions ? (
                      <span className="transport-delivery-quantity-fields__dims" data-testid={`transport-delivery-dims-${cargo.orderProductId}`}>{dimensions}</span>
                    ) : null}
                    <div className="transport-delivery-quantity-fields__meta">
                      {orderNumber ? <span>{orderNumber}</span> : null}
                      <Text type="secondary">{t('transport:deliveryConfirmation.planned', {
                        quantity: formatLocalizedQuantity(plannedQuantity, i18n.language),
                        unit,
                      })}</Text>
                    </div>
                  </div>
                  <Form.Item name={[field.name, 'orderProductId']} hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item
                    className="transport-delivery-quantity-fields__input"
                    name={[field.name, 'deliveredQuantity']}
                    label={t('transport:deliveryConfirmation.actual')}
                    rules={[
                      { required: true, message: t('transport:deliveryConfirmation.required') },
                      {
                        validator: async (_, value) => {
                          const quantity = Number(value);
                          if (
                            !Number.isFinite(quantity)
                            || quantity < 0
                          ) {
                            throw new Error(t('transport:deliveryConfirmation.range'));
                          }
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      min={0}
                      precision={3}
                      step={0.001}
                      {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                      formatter={(value, info) => (
                        info.userTyping ? info.input : formatDeliveryQuantityInput(value)
                      )}
                      addonAfter={unit}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                  <Form.Item noStyle shouldUpdate>
                    {({ getFieldValue }) => {
                      const deliveredQuantity = Number(
                        getFieldValue([name, field.name, 'deliveredQuantity']),
                      );
                      if (isShortDelivery(plannedQuantity, deliveredQuantity)) {
                        // „Nie jechało tym autem” with 0 removes the line; the
                        // last line of a ZK stays (take the ZK off the truck).
                        const items = (getFieldValue(name) || []) as TransportDeliveryFormItem[];
                        const othersOfOrderStay = cargoItems.some((other, otherIndex) => (
                          otherIndex !== index
                          && other.orderId === cargo.orderId
                          && other.orderProductId
                          && !(items[otherIndex]?.shortfall === 'NOT_ON_TRUCK' && Number(items[otherIndex]?.deliveredQuantity) === 0)
                        ));
                        const cannotLeave = deliveredQuantity === 0 && !othersOfOrderStay;
                        return (
                          <Form.Item
                            className="transport-delivery-quantity-fields__shortfall"
                            name={[field.name, 'shortfall']}
                            label={t('transport:deliveryConfirmation.shortfallQuestion')}
                            extra={cannotLeave ? t('transport:deliveryConfirmation.shortfallLastLine') : undefined}
                            rules={[{
                              validator: async (_, value) => {
                                if (value !== 'NOT_ARRIVED' && value !== 'NOT_ON_TRUCK') {
                                  throw new Error(t('transport:deliveryConfirmation.shortfallRequired'));
                                }
                                if (value === 'NOT_ON_TRUCK' && cannotLeave) {
                                  throw new Error(t('transport:deliveryConfirmation.shortfallLastLine'));
                                }
                              },
                            }]}
                          >
                            <Radio.Group data-testid={`transport-delivery-shortfall-${cargo.orderProductId}`}>
                              <Radio value="NOT_ARRIVED">{t('transport:deliveryConfirmation.shortfallNotArrived')}</Radio>
                              <Radio value="NOT_ON_TRUCK" disabled={cannotLeave}>
                                {deliveredQuantity === 0
                                  ? t('transport:deliveryConfirmation.shortfallNotOnTruckRemove')
                                  : t('transport:deliveryConfirmation.shortfallNotOnTruckTrim')}
                              </Radio>
                            </Radio.Group>
                          </Form.Item>
                        );
                      }
                      if (!Number.isFinite(deliveredQuantity) || deliveredQuantity <= plannedQuantity) {
                        return null;
                      }
                      return (
                        <Alert
                          className="transport-delivery-quantity-fields__over-plan"
                          type="warning"
                          showIcon
                          message={t('transport:deliveryConfirmation.overPlan', {
                            difference: formatLocalizedQuantity(
                              deliveredQuantity - plannedQuantity,
                              i18n.language,
                            ),
                            unit,
                          })}
                        />
                      );
                    }}
                  </Form.Item>
                </div>
              );
            })}
          </div>
        )}
      </Form.List>
    </section>
  );
};

export default TransportDeliveryQuantityFields;
